import type { SearchResponse, SearchResponseSettings } from "../components/ProductSearchForm";

export interface ProductDetailResponse extends SearchResponse {
    settings?: SearchResponseSettings;
}

export const emptyProductDetailResponse: ProductDetailResponse = {
    total_pages: 1,
    current_page: 1,
    total_results: 0,
    results: [],
    settings: {},
};

const productDetailResponseCache = new Map<string, ProductDetailResponse>();
const inflightProductDetailRequests = new Map<string, Promise<ProductDetailResponse>>();

const buildProductDetailAjaxUrl = (productIdentifier: string): string => {
    const normalizedIdentifier = String(productIdentifier || '').trim();
    const ajaxUrl = String((window as any)?.wekaApiSettings?.ajax_url || '/wp-admin/admin-ajax.php').trim() || '/wp-admin/admin-ajax.php';
    const ajaxNonce = String((window as any)?.wekaApiSettings?.ajax_nonce || '').trim();

    try {
        const url = typeof window === 'undefined'
            ? new URL(ajaxUrl, 'http://localhost')
            : new URL(ajaxUrl, window.location.origin);
        url.searchParams.set('action', 'weka_b2b_get_product_detail');
        url.searchParams.set('product_identifier', normalizedIdentifier);
        if (ajaxNonce !== '') {
            url.searchParams.set('nonce', ajaxNonce);
        }

        return `${url.pathname}${url.search}`;
    } catch {
        const query = new URLSearchParams();
        query.set('action', 'weka_b2b_get_product_detail');
        query.set('product_identifier', normalizedIdentifier);
        if (ajaxNonce !== '') {
            query.set('nonce', ajaxNonce);
        }

        return `${ajaxUrl}${ajaxUrl.includes('?') ? '&' : '?'}${query.toString()}`;
    }
};

export const normalizeProductDetailResponse = (payload: unknown): ProductDetailResponse => {
    const source = payload && typeof payload === 'object'
        ? (payload as ProductDetailResponse)
        : emptyProductDetailResponse;

    return {
        total_pages: Number(source?.total_pages || 1),
        current_page: Number(source?.current_page || 1),
        total_results: Number(source?.total_results || 0),
        results: Array.isArray(source?.results) ? source.results : [],
        settings: source?.settings && typeof source.settings === 'object' ? source.settings : {},
    };
};

export const fetchProductDetail = async (productIdentifier: string, signal?: AbortSignal): Promise<ProductDetailResponse> => {
    const response = await fetch(buildProductDetailAjaxUrl(productIdentifier), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
        },
        signal,
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
        const message = String((payload as any)?.data?.message || (payload as any)?.message || '').trim()
            || 'Unable to load this product right now.';
        throw new Error(message);
    }

    return normalizeProductDetailResponse(payload);
};

export const fetchCachedProductDetail = async (productIdentifier: string): Promise<ProductDetailResponse> => {
    const cacheKey = String(productIdentifier || '').trim();
    if (cacheKey === '') {
        return emptyProductDetailResponse;
    }

    const cachedResponse = productDetailResponseCache.get(cacheKey);
    if (cachedResponse) {
        return cachedResponse;
    }

    const inflightRequest = inflightProductDetailRequests.get(cacheKey);
    if (inflightRequest) {
        return inflightRequest;
    }

    const request = fetchProductDetail(cacheKey)
        .then((response) => {
            productDetailResponseCache.set(cacheKey, response);
            return response;
        })
        .finally(() => {
            inflightProductDetailRequests.delete(cacheKey);
        });

    inflightProductDetailRequests.set(cacheKey, request);

    return request;
};
